/**
 * Board Layout Context
 * Provides a BoardLayoutStore instance via React Context.
 * Convenience hooks use useSelector for granular subscriptions.
 *
 * Also provides ViewControlsCtx for header/sidebar/task-detail controls,
 * so that components (including the toolbar) can access resize/toggle
 * actions without direct store coupling.
 */

import React, { createContext, useContext, type ReactNode } from 'react';
import { useSelector } from '@xstate/store/react';
import type { Task } from '../../types';
import type { TaskDetailLevel } from '../hooks/useBoardTaskDetailLevel';
import type { HeaderRowId } from '../hooks/useBoardHeaderResize';
import type { BoardLayoutStore } from './boardLayoutStore';

// ============================================
// Board Layout Store Context
// ============================================

const BoardLayoutCtx = createContext<BoardLayoutStore | null>(null);

// ============================================
// View Controls Context
// ============================================

export interface HeaderControls {
  handleResizeStart: (e: React.MouseEvent) => void;
  mode: 'auto' | 'manual';
  toggleRow: (row: 'jahre' | 'monate' | 'tage') => void;
  setAutoMode: () => void;
  isRowVisible: (row: HeaderRowId) => boolean;
}

export interface SidebarControls {
  handleResizeStart: (e: React.MouseEvent) => void;
  handleDoubleClick: () => void;
  toggleCollapse: () => void;
}

export interface TaskDetailControls {
  detailLevel: TaskDetailLevel;
  rowHeight: number;
  padding: number;
  isResizing: boolean;
  setLevel: (level: TaskDetailLevel) => void;
  mode: 'auto' | 'manual';
  setAutoMode: () => void;
  handleResizeStart: (e: React.MouseEvent) => void;
}

export interface ViewControls {
  header: HeaderControls;
  sidebar: SidebarControls;
  taskDetail: TaskDetailControls;
}

const ViewControlsCtx = createContext<ViewControls | null>(null);

// ============================================
// Provider
// ============================================

export function BoardLayoutProvider({
  store,
  viewControls,
  children,
}: {
  store: BoardLayoutStore;
  viewControls?: ViewControls;
  children: ReactNode;
}) {
  return (
    <BoardLayoutCtx.Provider value={store}>
      <ViewControlsCtx.Provider value={viewControls ?? null}>
        {children}
      </ViewControlsCtx.Provider>
    </BoardLayoutCtx.Provider>
  );
}

// ============================================
// View Controls Hooks
// ============================================

export function useHeaderControls(): HeaderControls {
  const ctx = useContext(ViewControlsCtx);
  if (!ctx) throw new Error('useHeaderControls must be used within a BoardLayoutProvider with viewControls');
  return ctx.header;
}

export function useSidebarControls(): SidebarControls {
  const ctx = useContext(ViewControlsCtx);
  if (!ctx) throw new Error('useSidebarControls must be used within a BoardLayoutProvider with viewControls');
  return ctx.sidebar;
}

export function useTaskDetailControls(): TaskDetailControls {
  const ctx = useContext(ViewControlsCtx);
  if (!ctx) throw new Error('useTaskDetailControls must be used within a BoardLayoutProvider with viewControls');
  return ctx.taskDetail;
}

// ============================================
// Board Layout Store Hook
// ============================================

export function useBoardLayoutStore(): BoardLayoutStore {
  const store = useContext(BoardLayoutCtx);
  if (!store) {
    throw new Error('useBoardLayoutStore must be used within a BoardLayoutProvider');
  }
  return store;
}

// ============================================
// Layout Value Hooks (granular subscriptions)
// ============================================

export function useDayWidth(): number {
  return useSelector(useBoardLayoutStore(), s => s.context.dayWidth);
}

export function useRowHeight(): number {
  return useSelector(useBoardLayoutStore(), s => s.context.rowHeight);
}

export function useHeaderHeight(): number {
  return useSelector(useBoardLayoutStore(), s => s.context.headerHeight);
}

export function useColumnYOffsets(): Record<string, number> {
  return useSelector(useBoardLayoutStore(), s => s.context.columnYOffsets);
}

export function useRowAssignments(): Record<string, Map<string, number>> {
  return useSelector(useBoardLayoutStore(), s => s.context.rowAssignments);
}

export function useTasksByColumn(): Record<string, Task[]> {
  return useSelector(useBoardLayoutStore(), s => s.context.tasksByColumn);
}

export function useLayoutTotalHeight(): number {
  return useSelector(useBoardLayoutStore(), s => s.context.totalHeight);
}

export function useLayoutTotalDays(): number {
  return useSelector(useBoardLayoutStore(), s => s.context.totalDays);
}

export function useVisibleWeeks(): { kw: number; year: number; startDay: number; weekIndex: number }[] {
  return useSelector(useBoardLayoutStore(), s => s.context.visibleWeeks);
}

// Sidebar hooks
export function useSidebarWidth(): number {
  return useSelector(useBoardLayoutStore(), s => s.context.sidebarWidth);
}

export function useIsSidebarCollapsed(): boolean {
  return useSelector(useBoardLayoutStore(), s => s.context.isCollapsed);
}

export function useIsSidebarResizing(): boolean {
  return useSelector(useBoardLayoutStore(), s => s.context.isSidebarResizing);
}

// Detail hooks
export function useDetailLevel(): TaskDetailLevel {
  return useSelector(useBoardLayoutStore(), s => s.context.detailLevel);
}

export function useIsDetailResizing(): boolean {
  return useSelector(useBoardLayoutStore(), s => s.context.isDetailResizing);
}

// Header config hooks
export function useVisibleHeaderRows(): HeaderRowId[] {
  return useSelector(useBoardLayoutStore(), s => s.context.visibleRows);
}

export function useIsHeaderResizing(): boolean {
  return useSelector(useBoardLayoutStore(), s => s.context.isHeaderResizing);
}

export function useHeaderMode(): 'auto' | 'manual' {
  return useSelector(useBoardLayoutStore(), s => s.context.headerMode);
}
